import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card } from '@/components/ui/card';
import { Check, ChevronsUpDown, Plus, Search, UserPlus, Users, Edit2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { WorkforceCache, type Profissao, type Colaborador } from '@/services/workforceCache';
import DialogAddWorkforceTeam, { type WorkforceTeam } from './DialogAddWorkforceTeam';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DialogAddWorkforceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (workforceData: any) => void;
  editingData?: any;
  alreadyAddedTeamNames?: string[];
}

interface AddUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ open, onOpenChange, onSave }) => {
  const [activeTab, setActiveTab] = useState<'colaborador' | 'profissao'>('colaborador');
  const [colaboradorData, setColaboradorData] = useState({
    nome: '',
    profissao: '',
    telefone: '',
    email: '',
    endereco: '',
    observacoes: '',
    ativo: true
  });
  const [profissaoData, setProfissaoData] = useState({
    nome: '',
    descricao: ''
  });
  const { toast } = useToast();

  const profissoes = useMemo(() => WorkforceCache.getProfissoes(), []);

  const handleSaveColaborador = () => {
    if (!colaboradorData.nome || !colaboradorData.profissao) {
      toast({
        title: "Erro",
        description: "Nome e profissão são obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    WorkforceCache.addColaborador({
      ...colaboradorData,
      id: '',
      createdAt: '',
      updatedAt: ''
    });

    setColaboradorData({
      nome: '',
      profissao: '',
      telefone: '',
      email: '',
      endereco: '',
      observacoes: '',
      ativo: true
    });

    toast({
      title: "Sucesso",
      description: "Colaborador adicionado com sucesso!"
    });

    onSave();
    onOpenChange(false);
  };

  const handleSaveProfissao = () => {
    if (!profissaoData.nome) {
      toast({
        title: "Erro",
        description: "Nome da profissão é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    WorkforceCache.addProfissao({
      ...profissaoData,
      id: '',
      createdAt: '',
      updatedAt: ''
    });

    setProfissaoData({
      nome: '',
      descricao: ''
    });

    toast({
      title: "Sucesso",
      description: "Profissão adicionada com sucesso!"
    });

    onSave();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Novo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex border rounded-lg">
            <button
              className={cn(
                "flex-1 py-2 px-4 text-sm rounded-l-lg transition-colors",
                activeTab === 'colaborador' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
              onClick={() => setActiveTab('colaborador')}
            >
              Colaborador
            </button>
            <button
              className={cn(
                "flex-1 py-2 px-4 text-sm rounded-r-lg transition-colors",
                activeTab === 'profissao' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
              onClick={() => setActiveTab('profissao')}
            >
              Profissão
            </button>
          </div>

          {activeTab === 'colaborador' ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={colaboradorData.nome}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <Label htmlFor="profissao">Profissão *</Label>
                <Input
                  id="profissao"
                  value={colaboradorData.profissao}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, profissao: e.target.value }))}
                  placeholder="Ex: Pedreiro"
                  list="profissoes-list"
                />
                <datalist id="profissoes-list">
                  {profissoes.map(p => (
                    <option key={p.id} value={p.nome} />
                  ))}
                </datalist>
              </div>
              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={colaboradorData.telefone}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={colaboradorData.email}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="profissao-nome">Nome da Profissão *</Label>
                <Input
                  id="profissao-nome"
                  value={profissaoData.nome}
                  onChange={(e) => setProfissaoData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Pedreiro"
                />
              </div>
              <div>
                <Label htmlFor="profissao-descricao">Descrição</Label>
                <Input
                  id="profissao-descricao"
                  value={profissaoData.descricao}
                  onChange={(e) => setProfissaoData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição da profissão"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={activeTab === 'colaborador' ? handleSaveColaborador : handleSaveProfissao}>
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DialogAddWorkforce: React.FC<DialogAddWorkforceProps> = ({
  open,
  onOpenChange,
  onAdd,
  editingData,
  alreadyAddedTeamNames = []
}) => {
  const [selectedItem, setSelectedItem] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();

  // Pending items state
  const [pendingItems, setPendingItems] = useState<any[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Teams state
  const [teams, setTeams] = useState<WorkforceTeam[]>([
    { 
      id: "1", 
      name: "Equipe A", 
      ownership: "own", 
      members: [
        { id: "1", nome: "João Silva", funcao: "Pedreiro", quantidade: 2, type: "colaborador", colaboradorId: "1" },
        { id: "2", nome: "Maria Santos", funcao: "Servente", quantidade: 1, type: "colaborador", colaboradorId: "2" },
        { id: "3", nome: "Carpinteiro", funcao: "Carpinteiro", quantidade: 1, type: "profissao", profissaoId: "3" }
      ], 
      notes: "" 
    },
    { 
      id: "2", 
      name: "Equipe B", 
      ownership: "own", 
      members: [
        { id: "4", nome: "Carlos Lima", funcao: "Eletricista", quantidade: 1, type: "colaborador", colaboradorId: "4" },
        { id: "5", nome: "Pintor", funcao: "Pintor", quantidade: 3, type: "profissao", profissaoId: "5" }
      ], 
      notes: "" 
    },
    { 
      id: "3", 
      name: "Equipe Principal", 
      ownership: "own", 
      members: [], 
      notes: "" 
    }
  ]);
  const [selectedWorkforceTeam, setSelectedWorkforceTeam] = useState("sem-equipe");
  const [isWorkforceTeamOpen, setIsWorkforceTeamOpen] = useState(false);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<WorkforceTeam | undefined>(undefined);
  
  // Team selection and addition states
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [addedTeams, setAddedTeams] = useState<(WorkforceTeam & { editableMembers: any[] })[]>([]);
  const [activeTab, setActiveTab] = useState<'individuais' | 'equipes'>('individuais');

  // Form states
  const [workforceData, setWorkforceData] = useState({
    quantity: 1,
    employeeType: 'direta' as 'direta' | 'terceirizada',
    observations: ''
  });

  // Reset function to clear all temporary lists
  const resetAllLists = () => {
    setPendingItems([]);
    setSelectedTeamIds([]);
    setAddedTeams([]);
    setSelectedItem("");
    setWorkforceData({
      quantity: 1,
      employeeType: 'direta' as 'direta' | 'terceirizada',
      observations: ''
    });
    setSelectedWorkforceTeam("sem-equipe");
    setActiveTab('individuais');
    setSearchTerm("");
    setEditingIndex(null);
    setComboOpen(false);
    setIsWorkforceTeamOpen(false);
  };

  // Custom handler for modal close to ensure reset
  const handleModalClose = (open: boolean) => {
    if (!open) {
      resetAllLists();
    }
    onOpenChange(open);
  };

  // Buscar dados do cache
  const profissoes = useMemo(() => WorkforceCache.getProfissoes(), [refreshKey]);
  const colaboradores = useMemo(() => WorkforceCache.getColaboradores().filter(c => c.ativo), [refreshKey]);

  // Combinar profissões e colaboradores para a lista
  const allItems = useMemo(() => {
    const items = [
      {
        id: 'add-new',
        nome: 'Adicionar Novo Colaborador',
        type: 'action',
        profissao: ''
      }
    ];

    // Adicionar profissões primeiro
    profissoes.forEach(p => {
      items.push({
        id: `profissao-${p.id}`,
        nome: p.nome,
        type: 'profissao',
        profissao: p.nome
      });
    });

    // Depois colaboradores
    colaboradores.forEach(c => {
      items.push({
        id: `colaborador-${c.id}`,
        nome: c.nome,
        type: 'colaborador',
        profissao: c.profissao
      });
    });

    return items;
  }, [profissoes, colaboradores]);

  // Initialize form when dialog opens (only reset on first open, not during edits)
  const [dialogJustOpened, setDialogJustOpened] = useState(false);
  
  useEffect(() => {
    if (open && !dialogJustOpened) {
      // Mark that dialog just opened
      setDialogJustOpened(true);
      
      if (editingData) {
        // Check if this is a team edit
        if (editingData.isTeamEdit && editingData.teamMembers) {
          console.log('Team edit mode - loading all team members:', editingData.teamMembers);
          // Load all team members into pending list
          const teamItems = editingData.teamMembers.map((member: any) => ({
            id: crypto.randomUUID(),
            team: member.team || member.assignedTeam,
            function: member.function,
            name: member.name,
            type: member.type,
            assignedTeam: member.assignedTeam || member.team,
            quantity: member.quantity,
            employeeType: member.type || 'direta',
            observations: member.observations || ''
          }));
          setPendingItems(teamItems);
        } else {
          // When editing a single item, pre-populate the pending list with the editing item
          setPendingItems([{
            id: crypto.randomUUID(),
            team: editingData.team,
            function: editingData.function,
            name: editingData.name,
            type: editingData.type,
            assignedTeam: editingData.assignedTeam,
            quantity: editingData.quantity,
            employeeType: editingData.type,
            observations: editingData.observations
          }]);
        }
      } else {
        // Reset form and pending list when adding new
        setPendingItems([]);
      }
      
      // Only reset form fields on initial dialog open
      setSelectedItem("");
      setSelectedWorkforceTeam("sem-equipe");
      setWorkforceData({
        quantity: 1,
        employeeType: 'direta',
        observations: ''
      });
      setEditingIndex(null);
    } else if (!open && dialogJustOpened) {
      // Reset the flag when dialog closes
      setDialogJustOpened(false);
    }
  }, [open, editingData, dialogJustOpened]);

  // Filtrar itens por busca
  const filteredItems = useMemo(() => {
    if (!searchTerm) return allItems;
    
    return allItems.filter(item => 
      item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.profissao.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allItems, searchTerm]);

  const addToPendingList = () => {
    console.log('addToPendingList called, editingIndex:', editingIndex);
    console.log('Current workforceData:', workforceData);
    
    if (!selectedItem || selectedItem === 'add-new') {
      toast({
        title: "Erro",
        description: "Selecione um colaborador ou profissão.",
        variant: "destructive"
      });
      return;
    }

    const item = allItems.find(i => i.id === selectedItem);
    if (!item) return;

    const selectedTeamName = selectedWorkforceTeam === "sem-equipe" 
      ? "Sem equipe" 
      : teams.find(t => t.id === selectedWorkforceTeam)?.name || "Sem equipe";

    const isColaborador = item.type === 'colaborador';
    const workforceItem = {
      id: editingIndex !== null ? pendingItems[editingIndex].id : crypto.randomUUID(),
      team: item.nome,
      function: item.profissao,
      name: isColaborador ? item.nome : "-",
      assignedTeam: selectedTeamName,
      quantity: workforceData.quantity,
      employeeType: workforceData.employeeType,
      observations: workforceData.observations || ""
    };
    
    console.log('Created workforceItem:', workforceItem);

    if (editingIndex !== null) {
      // Update existing item
      console.log('Updating existing item at index:', editingIndex);
      console.log('Old item:', pendingItems[editingIndex]);
      const newPendingItems = [...pendingItems];
      newPendingItems[editingIndex] = workforceItem;
      setPendingItems(newPendingItems);
      console.log('New item:', workforceItem);
      setEditingIndex(null);
    } else {
      // Add new item
      console.log('Adding new item');
      setPendingItems(prev => [...prev, workforceItem]);
    }

    // Reset form
    setSelectedItem("");
    setSelectedWorkforceTeam("sem-equipe");
    setWorkforceData({
      quantity: 1,
      employeeType: 'direta',
      observations: ''
    });

    toast({
      title: "Sucesso",
      description: editingIndex !== null ? "Item atualizado na lista!" : "Item adicionado à lista!"
    });
  };

  const editPendingItem = (index: number) => {
    console.log('editPendingItem called with index:', index);
    const item = pendingItems[index];
    console.log('Editing item:', item);
    
    // Find the item in allItems
    let itemToSelect = '';
    if (item.name && item.name !== '-') {
      // It's a colaborador
      const colaboradorItem = allItems.find(ai => 
        ai.type === 'colaborador' && ai.nome === item.name
      );
      if (colaboradorItem) itemToSelect = colaboradorItem.id;
    } else {
      // It's a profissao
      const profissaoItem = allItems.find(ai => 
        ai.type === 'profissao' && ai.nome === item.function
      );
      if (profissaoItem) itemToSelect = profissaoItem.id;
    }
    
    console.log('Setting selectedItem to:', itemToSelect);
    setSelectedItem(itemToSelect);
    
    // Set team
    if (item.assignedTeam === "Sem equipe") {
      setSelectedWorkforceTeam("sem-equipe");
    } else {
      const teamItem = teams.find(t => t.name === item.assignedTeam);
      if (teamItem) {
        setSelectedWorkforceTeam(teamItem.id);
      }
    }
    
    // Set form data
    console.log('Setting workforceData with quantity:', item.quantity);
    setWorkforceData({
      quantity: item.quantity || 1,
      employeeType: item.employeeType || 'direta',
      observations: item.observations || ''
    });
    
    console.log('Setting editingIndex to:', index);
    setEditingIndex(index);
  };

  const removePendingItem = (index: number) => {
    setPendingItems(prev => prev.filter((_, i) => i !== index));
    toast({
      title: "Sucesso",
      description: "Item removido da lista!"
    });
  };

  const confirmAllItems = () => {
    if (pendingItems.length === 0) {
      toast({
        title: "Erro",
        description: "Adicione pelo menos um item à lista.",
        variant: "destructive"
      });
      return;
    }

    // Add all pending items with type: "individual"
    pendingItems.forEach(item => {
      onAdd({
        ...item,
        type: "individual"
      });
    });

    resetAllLists();
    onOpenChange(false);
    
    toast({
      title: "Sucesso",
      description: `${pendingItems.length} item(s) adicionado(s) com sucesso!`
    });
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSaveTeam = (team: WorkforceTeam) => {
    const teamWithId = { ...team, id: team.id || `team_${Date.now()}` };
    
    if (editingTeam) {
      // Editing existing team
      setTeams(prev => prev.map(t => 
        t.id === editingTeam.id 
          ? teamWithId
          : t
      ));
      toast({
        title: "Sucesso",
        description: "Equipe atualizada com sucesso!",
      });
    } else {
      // Adding new team
      setTeams(prev => [...prev, teamWithId]);
      toast({
        title: "Sucesso",
        description: "Equipe adicionada com sucesso!",
      });
    }
    
    setIsTeamDialogOpen(false);
    setEditingTeam(undefined);
  };

  // Team selection functions
  const toggleTeamSelection = (teamId: string) => {
    setSelectedTeamIds(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const addSelectedTeams = () => {
    const teamsToAdd = teams.filter(team => selectedTeamIds.includes(team.id));
    const duplicates = teamsToAdd.filter(team => 
      addedTeams.some(added => added.id === team.id)
    );

    if (duplicates.length > 0) {
      toast({
        title: "Erro",
        description: `Equipe${duplicates.length > 1 ? 's' : ''} ${duplicates.map(t => t.name).join(', ')} já adicionada${duplicates.length > 1 ? 's' : ''}.`,
        variant: "destructive"
      });
      return;
    }

    const newAddedTeams = teamsToAdd.map(team => ({
      ...team,
      editableMembers: (team.members || []).map(member => ({
        ...member,
        currentQuantity: member.quantidade || 1
      }))
    }));

    setAddedTeams(prev => [...prev, ...newAddedTeams]);
    setSelectedTeamIds([]);
    
    toast({
      title: "Sucesso",
      description: `${teamsToAdd.length} equipe${teamsToAdd.length > 1 ? 's' : ''} adicionada${teamsToAdd.length > 1 ? 's' : ''} à lista!`
    });
  };

  const removeAddedTeam = (teamId: string) => {
    setAddedTeams(prev => prev.filter(team => team.id !== teamId));
    toast({
      title: "Sucesso",
      description: "Equipe removida da lista!"
    });
  };

  const updateMemberQuantity = (teamId: string, memberId: string, newQuantity: number) => {
    setAddedTeams(prev => prev.map(team => 
      team.id === teamId 
        ? {
            ...team,
            editableMembers: team.editableMembers.map(member =>
              member.id === memberId 
                ? { ...member, currentQuantity: Math.max(0, newQuantity) }
                : member
            )
          }
        : team
    ));
  };

  const confirmTeams = () => {
    if (addedTeams.length === 0) {
      toast({
        title: "Erro",
        description: "Adicionar pelo menos uma equipe à lista.",
        variant: "destructive"
      });
      return;
    }

    // Chamar onAdd() apenas uma vez por equipe com dados consolidados
    addedTeams.forEach(team => {
      // Calcular total de membros da equipe
      const totalMembers = team.editableMembers.reduce((total, member) => 
        total + (member.currentQuantity || 0), 0
      );
      
      // Montar array de membros com dados consolidados
      const membersData = team.editableMembers
        .filter(member => member.currentQuantity > 0)
        .map(member => ({
          name: member.type === 'colaborador' ? member.nome : member.funcao,
          role: member.funcao,
          qty: member.currentQuantity
        }));

      // Enviar um único objeto de equipe
      onAdd({
        type: "team",
        teamId: team.id,
        teamName: team.name,
        totalMembers: totalMembers,
        members: membersData,
        employeeType: team.ownership === 'own' ? 'direta' : 'terceirizada',
        observations: team.notes || ""
      });
    });

    resetAllLists();
    onOpenChange(false);
    
    toast({
      title: "Sucesso",
      description: `${addedTeams.length} equipe${addedTeams.length > 1 ? 's' : ''} confirmada${addedTeams.length > 1 ? 's' : ''} com sucesso!`
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingData ? 'Editar Mão de Obra' : 'Adicionar Mão de Obra'}</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'individuais' | 'equipes')} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 shrink-0">
              <TabsTrigger value="individuais">Individuais</TabsTrigger>
              <TabsTrigger value="equipes">Equipes</TabsTrigger>
            </TabsList>

            <TabsContent value="individuais" className="flex-1 flex flex-col space-y-4 pb-20 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <Label>Selecionar Profissão ou Colaborador</Label>
                  <Popover open={comboOpen} onOpenChange={setComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={comboOpen}
                        className="w-full justify-between"
                      >
                        {selectedItem === 'add-new' 
                          ? "Adicionar Novo Colaborador"
                          : selectedItem 
                          ? allItems.find(i => i.id === selectedItem)?.nome
                          : "Buscar profissões e colaboradores..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <div className="flex items-center border-b px-3">
                          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                          <input
                            placeholder="Buscar por nome ou profissão..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </div>
                        <CommandList>
                          <CommandEmpty>
                            Nenhum resultado encontrado.
                          </CommandEmpty>
                          <CommandGroup>
                            {filteredItems.map((item) => (
                              <CommandItem
                                key={item.id}
                                value={item.nome}
                                onSelect={() => {
                                  if (item.id === 'add-new') {
                                    setAddUserModalOpen(true);
                                    setComboOpen(false);
                                  } else {
                                    setSelectedItem(item.id);
                                    setComboOpen(false);
                                  }
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedItem === item.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {item.id === 'add-new' ? (
                                      <Plus className="w-4 h-4 text-primary" />
                                    ) : null}
                                    <div className="font-medium">{item.nome}</div>
                                  </div>
                                  {item.profissao && item.id !== 'add-new' && (
                                    <div className="text-sm text-muted-foreground">
                                      {item.type === 'profissao' ? 'Profissão' : item.profissao}
                                    </div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quantity">Quantidade</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={workforceData.quantity}
                      onChange={(e) => setWorkforceData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                </div>

                <div>
                  <Label>Tipo de Funcionário</Label>
                  <div className="flex border rounded-lg">
                    <button
                      className={cn(
                        "flex-1 py-2 px-4 text-sm rounded-l-lg transition-colors",
                        workforceData.employeeType === 'direta' 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                      onClick={() => setWorkforceData(prev => ({ ...prev, employeeType: 'direta' }))}
                    >
                      Próprio
                    </button>
                    <button
                      className={cn(
                        "flex-1 py-2 px-4 text-sm rounded-r-lg transition-colors",
                        workforceData.employeeType === 'terceirizada' 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                      onClick={() => setWorkforceData(prev => ({ ...prev, employeeType: 'terceirizada' }))}
                    >
                      Terceirizado
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={addToPendingList}
                  disabled={!selectedItem || selectedItem === 'add-new'}
                  className="w-full"
                >
                  {editingIndex !== null ? 'Atualizar Item' : 'Adicionar à Lista'}
                </Button>

                {pendingItems.length > 0 && (
                  <div>
                    <Label>Itens a Confirmar ({pendingItems.length})</Label>
                    <Card className="p-4 max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {pendingItems.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex-1">
                              <div className="font-medium">
                                {item.name !== '-' ? item.name : item.function}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {item.name !== '-' && `${item.function} • `}
                                Qtd: {item.quantity} • 
                                Equipe: {item.assignedTeam} • 
                                Tipo: {item.employeeType === 'direta' ? 'Próprio' : 'Terceirizado'}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => editPendingItem(index)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removePendingItem(index)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="equipes" className="flex-1 flex flex-col pb-20 overflow-y-auto">
              {/* Área 1: Lista de Equipes - Parte Superior */}
              <div className="flex flex-col border-b">
                <div className="flex items-center justify-between py-1 px-4 border-b bg-muted/30">
                  <div>
                    <h3 className="text-sm font-semibold">Selecionar Equipes</h3>
                    <p className="text-xs text-muted-foreground">
                      Clique para selecionar equipes
                    </p>
                  </div>
                  <Button onClick={() => setIsTeamDialogOpen(true)} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nova Equipe
                  </Button>
                </div>

                {/* Lista de Equipes com scroll interno */}
                <div className="max-h-[40vh] overflow-y-auto p-4">
                  {teams.length === 0 ? (
                    <Card className="p-6 text-center">
                      <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Nenhuma equipe criada</h3>
                      <p className="text-muted-foreground mb-4">
                        Crie equipes para organizar melhor sua mão de obra
                      </p>
                      <Button onClick={() => setIsTeamDialogOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Criar primeira equipe
                      </Button>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {teams.map((team) => {
                        const getMembersLegend = (members: any[]) => {
                          if (!members || members.length === 0) {
                            return "Sem membros";
                          }

                          const totalMembers = members.reduce((sum, member) => sum + (member.quantidade || 1), 0);
                          
                          const groupedMembers: { [key: string]: number } = {};
                          members.forEach(member => {
                            const key = member.funcao || 'Função não definida';
                            groupedMembers[key] = (groupedMembers[key] || 0) + (member.quantidade || 1);
                          });

                          const professionsList = Object.entries(groupedMembers).map(([profession, count]) => 
                            `${count} ${profession.toLowerCase()}${count > 1 ? 's' : ''}`
                          );

                          const displayList = professionsList.slice(0, 3);
                          const hasMore = professionsList.length > 3;
                          
                          let listText = '';
                          if (displayList.length === 1) {
                            listText = displayList[0];
                          } else if (displayList.length === 2) {
                            listText = displayList.join(' e ');
                          } else if (displayList.length > 2) {
                            const lastItem = displayList.pop();
                            listText = displayList.join(', ') + ' e ' + lastItem;
                          }
                          
                          return `Total (${totalMembers}): ${listText}${hasMore ? '...' : ''}`;
                        };

                        const isSelected = selectedTeamIds.includes(team.id);
                        const isAlreadyInRDO = alreadyAddedTeamNames.includes(team.name);
                        const isInAddedTeams = addedTeams.some(t => t.id === team.id);
                        const isDisabled = isAlreadyInRDO || isInAddedTeams;

                        const teamCard = (
                          <Card 
                            key={team.id} 
                            className={cn(
                              "p-2 transition-all border",
                              isDisabled 
                                ? "cursor-not-allowed" 
                                : isSelected 
                                ? "border-primary bg-primary/5 ring-1 ring-primary/20 cursor-pointer hover:bg-muted/50" 
                                : "border-border cursor-pointer hover:bg-muted/50"
                            )}
                            style={isDisabled ? { backgroundColor: '#F3F4F6' } : undefined}
                            onClick={() => !isDisabled && toggleTeamSelection(team.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <div className={cn(
                                  "flex h-6 w-6 items-center justify-center rounded-md shrink-0",
                                  isDisabled 
                                    ? "bg-gray-300" 
                                    : isSelected 
                                    ? "bg-primary text-primary-foreground" 
                                    : "bg-primary/10"
                                )}>
                                  <Users className={cn(
                                    "h-3.5 w-3.5",
                                    isDisabled && "text-gray-500"
                                  )} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h4 className={cn(
                                    "text-sm font-medium truncate",
                                    isDisabled && "text-[#9CA3AF]"
                                  )}>
                                    {team.name}
                                  </h4>
                                  <p className={cn(
                                    "text-xs text-muted-foreground truncate",
                                    isDisabled && "text-[#9CA3AF]"
                                  )}>
                                    {getMembersLegend(team.members || [])}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingTeam(team);
                                    setIsTeamDialogOpen(true);
                                  }}
                                  className="h-7 w-7 p-0"
                                  aria-label={`Editar equipe ${team.name}`}
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setTeams(prev => prev.filter(t => t.id !== team.id));
                                    toast({
                                      title: "Sucesso",
                                      description: "Equipe removida com sucesso"
                                    });
                                  }}
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                  aria-label={`Remover equipe ${team.name}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        );

                        return isDisabled ? (
                          <TooltipProvider key={team.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {teamCard}
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{isAlreadyInRDO ? "Equipe já adicionada ao RDO" : "Equipe já adicionada à lista"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : teamCard;
                      })}
                    </div>
                  )}
                </div>

                {/* Botão Adicionar Equipe - Sticky */}
                <div className="sticky bottom-0 p-4 bg-background border-t">
                  <Button 
                    onClick={addSelectedTeams}
                    disabled={selectedTeamIds.length === 0}
                    className="w-full"
                    aria-label={`Adicionar ${selectedTeamIds.length} equipe${selectedTeamIds.length > 1 ? 's' : ''}`}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Adicionar Equipe{selectedTeamIds.length > 1 ? 's' : ''} ({selectedTeamIds.length})
                  </Button>
                </div>
              </div>

              {/* Área 2: Equipes Adicionadas - Parte Inferior */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="py-1 px-4 bg-muted/30 border-b">
                  <h4 className="text-sm font-semibold">Equipes Adicionadas ({addedTeams.length})</h4>
                  <p className="text-xs text-muted-foreground">Ajuste as quantidades conforme necessário</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 max-h-[calc(35vh-80px)] pb-24">
                  {addedTeams.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="mx-auto h-8 w-8 mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma equipe adicionada</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {addedTeams.map((team) => (
                        <Card key={team.id} className="border">
                          {/* Cabeçalho do Acordeão */}
                          <div className="p-2 border-b bg-muted/20">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Users className="h-3.5 w-3.5 text-primary" />
                                <span className="text-sm font-medium">{team.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({team.editableMembers.reduce((sum, member) => sum + (member.currentQuantity || 0), 0)} pessoas)
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAddedTeam(team.id)}
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                aria-label={`Remover equipe ${team.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Lista de Membros */}
                          {team.editableMembers.length > 0 && (
                            <div className="p-2 space-y-1.5">
                              {team.editableMembers.map((member) => (
                                <div key={member.id} className="flex items-center justify-between py-1.5 px-2 bg-muted/30 rounded-md">
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium truncate">
                                      {member.type === 'colaborador' ? member.nome : member.funcao}
                                      <span className="text-xs text-muted-foreground ml-2">
                                        • {member.type === 'colaborador' ? member.funcao : 'Profissão'}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateMemberQuantity(team.id, member.id, (member.currentQuantity || 1) - 1)}
                                      disabled={(member.currentQuantity || 1) <= 0}
                                      className="h-6 w-6 p-0"
                                      aria-label="Diminuir quantidade"
                                    >
                                      -
                                    </Button>
                                    <span className="text-sm font-medium w-6 text-center">
                                      {member.currentQuantity || 0}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateMemberQuantity(team.id, member.id, (member.currentQuantity || 1) + 1)}
                                      className="h-6 w-6 p-0"
                                      aria-label="Aumentar quantidade"
                                    >
                                      +
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Action Bar - Fixed at bottom limit of popup */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-end space-x-2 p-4 bg-background border-t shadow-lg z-10">
            <Button variant="outline" onClick={() => handleModalClose(false)}>
              Cancelar
            </Button>
            {activeTab === 'individuais' ? (
              <Button 
                onClick={confirmAllItems}
                disabled={pendingItems.length === 0}
              >
                Confirmar ({pendingItems.length})
              </Button>
            ) : (
              <Button 
                onClick={confirmTeams}
                disabled={addedTeams.length === 0}
              >
                Confirmar ({addedTeams.length})
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AddUserModal 
        open={addUserModalOpen} 
        onOpenChange={setAddUserModalOpen}
        onSave={refreshData}
      />
      
      <DialogAddWorkforceTeam
        open={isTeamDialogOpen}
        onOpenChange={(open) => {
          setIsTeamDialogOpen(open);
          if (!open) setEditingTeam(undefined);
        }}
        collaborators={colaboradores.map(c => ({ id: c.id, name: c.nome, defaultRole: c.profissao }))}
        onSave={handleSaveTeam}
        initialTeam={editingTeam ? {
          id: editingTeam.id,
          name: editingTeam.name,
          ownership: editingTeam.ownership,
          members: editingTeam.members,
          notes: editingTeam.notes
        } : undefined}
      />
    </>
  );
};